import React from "react";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import { useForm, useFormState } from "react-hook-form";
import { formatDate } from "../../utils";
import { profileValidationRules } from "./validations";
import { ProfileFormComponentProps, ProfileFormData } from "./types";
import { FormInput, InputDate } from "../../components/Input";
import { DiscardPopUp } from "../../components/PopUp";
import { buildUnsavedChangesDetails } from "../../components/PopUp/confirmShared";
import { SaveBar } from "./components/SaveBar";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  IconContainer,
  TitleRow,
  FormGrid,
  FormGroup,
  Label,
  HelperText,
  Divider,
  ProfileSection,
  AvatarContainer,
  Avatar,
  ProfileInfo,
  ProfileName,
  Badge,
} from "./styles";

export const ProfileFormComponent: React.FC<ProfileFormComponentProps> = ({
  onUpdate,
  userDetails,
  onFormDirtyChange,
  saveFormRef,
  cancelFormRef,
}) => {
  const [showConfirm, setShowConfirm] = React.useState(false);

  const { control, reset, watch, handleSubmit } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      email: userDetails.email,
      phone: userDetails.phone ?? undefined,
      dateOfBirth: userDetails.dateOfBirth,
    },
    mode: "onBlur", // Validate on blur (first touch)
    reValidateMode: "onChange", // Re-validate in real-time after first blur
  });

  const { isDirty, dirtyFields, isSubmitting } = useFormState({ control });

  const firstName = watch("firstName");
  const lastName = watch("lastName");

  const dirtyCount = Object.keys(dirtyFields).length;
  const discardDetails = buildUnsavedChangesDetails(dirtyCount);

  // Notify parent of dirty state changes
  React.useEffect(() => {
    onFormDirtyChange?.(isDirty);
  }, [isDirty, onFormDirtyChange]);

  const onSubmit = React.useCallback(
    async (data: ProfileFormData) => {
      function pickDirty<T extends Record<string, any>>(
        data: T,
        dirty: Partial<Record<keyof T, boolean>>,
      ): Partial<T> {
        return (Object.keys(dirty) as (keyof T)[]).reduce((acc, key) => {
          if (key === "dateOfBirth" && data[key]) {
            acc[key] = formatDate(data[key]) as any;
          } else {
            acc[key] = data[key];
          }
          return acc;
        }, {} as Partial<T>);
      }

      const changedData = pickDirty(data, dirtyFields);
      const success = await onUpdate(changedData);
      if (success) {
        reset(data);
      }
    },
    [dirtyFields, onUpdate, reset],
  );

  const handleRevert = React.useCallback(() => {
    setShowConfirm(true);
  }, []);

  const confirmRevert = React.useCallback(() => {
    reset(userDetails as any);
    setShowConfirm(false);
  }, [reset, userDetails]);

  // Expose save and cancel methods to parent via refs
  React.useEffect(() => {
    if (saveFormRef) {
      saveFormRef.current = () => {
        if (isDirty) {
          handleSubmit(onSubmit)();
        }
      };
    }
    if (cancelFormRef) {
      cancelFormRef.current = () => {
        if (isDirty) {
          handleRevert();
        }
      };
    }
  }, [
    isDirty,
    handleSubmit,
    onSubmit,
    handleRevert,
    saveFormRef,
    cancelFormRef,
  ]);

  return (
    <Card className="profile">
      <CardHeader>
        <TitleRow>
          <IconContainer>
            <PersonOutlined />
          </IconContainer>
          <CardTitle>Profile Information</CardTitle>
        </TitleRow>
        <CardDescription>
          Manage your personal information and how others see you across the
          platform
        </CardDescription>
      </CardHeader>

      <ProfileSection>
        <AvatarContainer>
          <Avatar>{firstName?.charAt(0).toUpperCase()}</Avatar>
        </AvatarContainer>
        <ProfileInfo>
          <ProfileName>
            {firstName} {lastName}
          </ProfileName>
          <Badge>
            <span className="dot" aria-hidden="true" />
            Verified
          </Badge>
        </ProfileInfo>
      </ProfileSection>

      <Divider />

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGrid>
          <FormGroup>
            <Label>
              First Name<span className="required">*</span>
              <span className="field-tag tag-required">Required</span>
            </Label>
            <FormInput
              name="firstName"
              control={control}
              rules={profileValidationRules.firstName}
              disabled={isSubmitting}
              placeholder="Enter your first name"
              autoComplete="given-name"
            />
          </FormGroup>
          <FormGroup>
            <Label>
              Last Name<span className="required">*</span>
              <span className="field-tag tag-required">Required</span>
            </Label>
            <FormInput
              name="lastName"
              control={control}
              rules={profileValidationRules.lastName}
              disabled={isSubmitting}
              placeholder="Enter your last name"
              autoComplete="family-name"
            />
          </FormGroup>
          <FormGroup className="full-width">
            <Label>
              Email<span className="required">*</span>
              <span className="field-tag tag-required">Required</span>
            </Label>
            <FormInput
              type="email"
              name="email"
              control={control}
              rules={profileValidationRules.email}
              disabled={isSubmitting}
              placeholder="Enter your email"
              autoComplete="email"
            />
            <HelperText>
              We'll send you important account notifications to this address
            </HelperText>
          </FormGroup>
          <FormGroup className="full-width">
            <Label>
              Phone
              <span className="field-tag tag-optional">Optional</span>
            </Label>
            <FormInput
              type="tel"
              name="phone"
              control={control}
              rules={profileValidationRules.phone}
              disabled={isSubmitting}
              placeholder="+1 (555) 123-4567"
              autoComplete="tel"
            />
            <HelperText>Optional. Used for account recovery</HelperText>
          </FormGroup>
          <FormGroup className="full-width">
            <Label>
              Date of Birth<span className="required">*</span>
              <span className="field-tag tag-required">Required</span>
            </Label>
            <InputDate
              testId="date-of-birth"
              name="dateOfBirth"
              control={control}
              rules={profileValidationRules.dateOfBirth}
              disabled={isSubmitting}
              maxDate={new Date()}
              placeholder="YYYY/MM/DD"
              autoComplete="bday"
            />
            <HelperText>
              Your date of birth is used for age verification and never shared
              publicly
            </HelperText>
          </FormGroup>
        </FormGrid>
      </form>

      <SaveBar
        isDirty={isDirty}
        isSubmitting={isSubmitting}
        onSave={handleSubmit(onSubmit)}
        onDiscard={handleRevert}
      />

      <DiscardPopUp
        isOpen={showConfirm}
        setIsOpen={setShowConfirm}
        eyebrow="EDITING PROFILE"
        title="Discard your changes?"
        text="Your edits to your personal details haven't been saved yet. If you leave now they'll be lost — this can't be undone."
        details={discardDetails}
        handleOnDiscard={confirmRevert}
      />
    </Card>
  );
};

export const ProfileForm = React.memo(ProfileFormComponent);
